import { createContext, useContext, useState, type ReactNode } from "react";
import { create, useStore } from "zustand";
import type { EntityFilter } from "@/lib/api/shareable-entities";

export type HubView =
  | "list"
  | "create-note"
  | "preview-note"
  | "view-note"
  | "create-moment"
  | "preview-moment"
  | "view-moment"
  | "create-swap"
  | "surprise-swap"
  | "custom-swap"
  | "upload-swap"
  | "preview-swap"
  | "view-swap"
  | "view-hot-takes"
  | "view-connect-moment"
  | "view-deep-thoughts";

export interface DraftMomentPhoto {
  id: string;
  file: File;
  preview: string;
}

export interface DraftMoment {
  photos: DraftMomentPhoto[];
  caption: string;
}

export interface DraftSwap {
  promptId: string;
  photo: DraftMomentPhoto | null;
}

interface HubStore {
  /** When true (Action shell), never show Hub list — back/list navigates close Hub. */
  embedMode: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  currentView: HubView;
  selectedNoteId: string | null;
  selectedIndex: number | null;
  viewNavigationToken: number;
  draftNoteContent: string | null;
  draftMoment: DraftMoment | null;
  draftSwap: DraftSwap | null;
  navigationHistory: HubView[];
  activeTab: "new" | "all";
  activeFilter: EntityFilter;
  navigateToView: (
    view: HubView,
    noteId?: string | null,
    index?: number | null,
  ) => void;
  openToView: (
    view: HubView,
    noteId?: string | null,
    index?: number | null,
  ) => void;
  setSelectedCarouselIndex: (index: number) => void;
  setDraftNoteContent: (content: string | null) => void;
  setDraftMoment: (moment: DraftMoment | null) => void;
  setDraftSwap: (swap: DraftSwap | null) => void;
  setActiveTab: (tab: "new" | "all") => void;
  setActiveFilter: (filter: EntityFilter) => void;
  goBack: () => void;
  resetView: () => void;
}

const DEFAULT_STATE = {
  currentView: "list" as HubView,
  selectedNoteId: null,
  selectedIndex: null,
  viewNavigationToken: 0,
  draftNoteContent: null,
  draftMoment: null,
  draftSwap: null,
  navigationHistory: [] as HubView[],
  activeTab: "new" as const,
  activeFilter: "all" as EntityFilter,
};

function nextHistory(
  history: HubView[],
  currentView: HubView,
  nextView: HubView,
  embedMode: boolean,
): HubView[] {
  if (currentView === nextView) return history;
  // Action shell: never stack the legacy list under create/view flows.
  if (embedMode && currentView === "list") return history;
  return [...history, currentView];
}

const createHubStore = (embedMode: boolean) =>
  create<HubStore>((set, get) => ({
    embedMode,
    isOpen: false,
    ...DEFAULT_STATE,
    open: () => set({ isOpen: true }),
    close: () => {
      set({ isOpen: false, ...DEFAULT_STATE });
    },
    toggle: () => set((state) => ({ isOpen: !state.isOpen })),
    navigateToView: (
      view: HubView,
      noteId?: string | null,
      index?: number | null,
    ) => {
      if (get().embedMode && view === "list") {
        get().close();
        return;
      }
      const currentView = get().currentView;
      set((state) => ({
        currentView: view,
        selectedNoteId: noteId ?? null,
        selectedIndex: index ?? null,
        viewNavigationToken: state.viewNavigationToken + 1,
        navigationHistory: nextHistory(
          state.navigationHistory,
          currentView,
          view,
          state.embedMode,
        ),
      }));
    },
    openToView: (
      view: HubView,
      noteId?: string | null,
      index?: number | null,
    ) => {
      if (get().embedMode && view === "list") {
        get().close();
        return;
      }
      const currentView = get().currentView;
      const nextIndex = index ?? null;
      set((state) => ({
        isOpen: true,
        currentView: view,
        selectedNoteId: noteId ?? null,
        selectedIndex: nextIndex,
        viewNavigationToken: state.viewNavigationToken + 1,
        navigationHistory: nextHistory(
          state.navigationHistory,
          currentView,
          view,
          state.embedMode,
        ),
      }));
    },
    setSelectedCarouselIndex: (index: number) => {
      set({ selectedIndex: index });
    },
    setDraftNoteContent: (content: string | null) => {
      set({ draftNoteContent: content });
    },
    setDraftMoment: (moment: DraftMoment | null) => {
      set({ draftMoment: moment });
    },
    setDraftSwap: (swap: DraftSwap | null) => {
      set({ draftSwap: swap });
    },
    setActiveTab: (tab: "new" | "all") => {
      set({ activeTab: tab });
      if (tab === "new") {
        set({ activeFilter: "all" });
      }
    },
    setActiveFilter: (filter: EntityFilter) => {
      set({ activeFilter: filter });
    },
    goBack: () => {
      const { navigationHistory, embedMode: embedded } = get();
      if (navigationHistory.length > 0) {
        const previousView = navigationHistory[navigationHistory.length - 1];
        if (embedded && previousView === "list") {
          get().close();
          return;
        }
        set((state) => ({
          currentView: previousView,
          selectedNoteId: null,
          selectedIndex: null,
          navigationHistory: state.navigationHistory.slice(0, -1),
        }));
        return;
      }
      if (embedded) {
        get().close();
        return;
      }
      set({
        currentView: "list",
        selectedNoteId: null,
        selectedIndex: null,
      });
    },
    resetView: () => {
      set(DEFAULT_STATE);
    },
  }));

type HubStoreApi = ReturnType<typeof createHubStore>;

type HubStoreState = HubStore;

const HubStoreContext = createContext<HubStoreApi | null>(null);

export function HubStoreProvider({
  children,
  embedMode = false,
}: {
  children: ReactNode;
  /** Action / new mentor workflows shell — suppress legacy Hub list. */
  embedMode?: boolean;
}) {
  const [store] = useState<HubStoreApi>(() => createHubStore(embedMode));

  return (
    <HubStoreContext.Provider value={store}>
      {children}
    </HubStoreContext.Provider>
  );
}

export function useHubStore(): HubStoreState;
export function useHubStore<T>(selector: (state: HubStoreState) => T): T;

export function useHubStore<T>(selector?: (state: HubStoreState) => T) {
  const store = useContext(HubStoreContext);
  if (!store) {
    throw new Error("useHubStore must be used within HubStoreProvider");
  }
  return useStore(store, selector ?? ((state) => state as T));
}
